import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { auth } from '../firebase'
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  browserPopupRedirectResolver,
  updateProfile,
  reload,
  type User
} from 'firebase/auth'
import { useAppStore } from './appStore'

export const hasStoredAuthSession = (): boolean => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem('was_authenticated') === '1' || !!localStorage.getItem('auth_user_cache')
  } catch {
    return false
  }
}

export const clearStoredAuthSession = () => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem('was_authenticated')
    localStorage.removeItem('auth_user_cache')
  } catch {}
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isAuthReady = ref(false)
  const isRestoringSession = ref(hasStoredAuthSession())
  const isAccountBlocked = ref(false)
  const blockedDetails = ref<{ email: string; reason?: string } | null>(null)
  const redirectAuthError = ref<any>(null)
  const isAdmin = computed(() => user.value?.email === 'robin.terhorst.home@gmail.com')

  // Computed photo URL: checks user.photoURL, falling back to providerData (e.g. Google provider)
  const userPhotoURL = computed(() => {
    if (!user.value) return null
    if (user.value.photoURL) return user.value.photoURL
    const providerWithPhoto = user.value.providerData?.find(p => p?.photoURL)
    return providerWithPhoto?.photoURL || null
  })

  const syncUserProfilePhoto = async (targetUser?: User | null) => {
    const u = targetUser || user.value || auth.currentUser
    if (!u) return
    const providerPhoto = u.providerData?.find(p => p?.photoURL)?.photoURL
    if (providerPhoto && u.photoURL !== providerPhoto) {
      try {
        await updateProfile(u, { photoURL: providerPhoto })
        if (user.value) {
          user.value = { ...u } as User
        }
      } catch (err) {
        console.warn('[authStore] Failed to auto-sync photoURL to user profile:', err)
      }
    }
  }

  const refreshUserProfile = async () => {
    if (!auth.currentUser) return
    try {
      await reload(auth.currentUser)
      user.value = auth.currentUser
      await syncUserProfilePhoto(auth.currentUser)
    } catch (err) {
      console.warn('[authStore] Could not reload auth profile:', err)
      throw err
    }
  }

  let blockStatusUnsub: (() => void) | null = null
  let initPromise: Promise<void> | null = null
  let isLoggingOut = false
  let pendingRedirectUser: any = null

  // Initialize auth listener
  const initializeAuth = () => {
    if (initPromise) return initPromise

    initPromise = new Promise<void>((resolve) => {
      let resolved = false
      const safeResolve = () => {
        if (!resolved) {
          resolved = true
          isAuthReady.value = true
          resolve()
        }
      }

      // Only check redirect result if a redirect was actually initiated
      const hasRedirectPending = typeof window !== 'undefined' && (
        sessionStorage.getItem('auth_redirect_pending') ||
        Object.keys(sessionStorage).some(k => k.startsWith('firebase:redirectUser'))
      )

      // Fast-boot safety timeout: if network or token refresh stalls on poor internet (Lie-Fi),
      // mount the app within safety timeout using cached auth state so user never sees a white screen
      const hasStored = hasStoredAuthSession()
      const timeoutDuration = hasRedirectPending ? 3000 : (hasStored ? 6000 : 1000)
      const timeoutId = setTimeout(() => {
        if (!isAuthReady.value) {
          isRestoringSession.value = false
          const appStore = useAppStore()
          if (auth.currentUser && !user.value) {
            user.value = auth.currentUser
            appStore.initialize()
          } else if (!user.value) {
            clearStoredAuthSession()
            appStore.loadDemoData?.()
          }
          safeResolve()
        }
      }, timeoutDuration)

      let redirectPromise: Promise<void> | null = null
      if (hasRedirectPending) {
        try {
          sessionStorage.removeItem('auth_redirect_pending')
        } catch {}
        redirectPromise = getRedirectResult(auth, browserPopupRedirectResolver)
          .then(async (redirectCred) => {
            if (redirectCred?.user) {
              const { useAccessControl } = await import('../composables/useAccessControl')
              const { checkIfUserBlocked } = useAccessControl()
              const blockCheck = await checkIfUserBlocked({
                uid: redirectCred.user.uid,
                email: redirectCred.user.email
              })
              if (blockCheck.isBlocked) {
                isAccountBlocked.value = true
                blockedDetails.value = {
                  email: redirectCred.user.email || redirectCred.user.uid,
                  reason: blockCheck.reason || ''
                }
                user.value = null
                await signOut(auth).catch(() => {})
                const appStore = useAppStore()
                appStore.clearStore()
                return
              }
              pendingRedirectUser = redirectCred.user
              user.value = redirectCred.user
              syncUserProfilePhoto(redirectCred.user).catch(() => {})
              const appStore = useAppStore()
              appStore.initialize()
            }
          })
          .catch((redirectErr) => {
            console.error('Error handling redirect result:', redirectErr)
            redirectAuthError.value = redirectErr
          })
      }

      onAuthStateChanged(auth, async (currentUser) => {
        clearTimeout(timeoutId)
        isRestoringSession.value = false

        if (redirectPromise) {
          await redirectPromise.catch(() => {})
        }

        // If a logout operation is actively in progress, ignore incoming auth changes and ensure user is null
        if (isLoggingOut) {
          user.value = null
          safeResolve()
          return
        }

        // Always clean up previous block subscription
        if (blockStatusUnsub) {
          blockStatusUnsub()
          blockStatusUnsub = null
        }

        const effectiveUser = currentUser || pendingRedirectUser
        pendingRedirectUser = null

        if (effectiveUser) {
          const { useAccessControl } = await import('../composables/useAccessControl')
          const { checkIfUserBlocked, subscribeToUserBlockStatus } = useAccessControl()

          // Check if user or email is blocked on initial auth load
          const blockCheck = await checkIfUserBlocked({
            uid: effectiveUser.uid,
            email: effectiveUser.email
          })

          if (blockCheck.isBlocked) {
            isAccountBlocked.value = true
            blockedDetails.value = {
              email: effectiveUser.email || effectiveUser.uid,
              reason: blockCheck.reason || ''
            }
            user.value = null
            await signOut(auth).catch(() => {})
            const appStore = useAppStore()
            appStore.clearStore()

            safeResolve()
            return
          }

          // User is active and not blocked -> Start real-time block monitoring
          blockStatusUnsub = subscribeToUserBlockStatus(
            { uid: effectiveUser.uid, email: effectiveUser.email },
            async (details) => {
              if (isAccountBlocked.value) return
              isAccountBlocked.value = true
              blockedDetails.value = {
                email: details.email,
                reason: details.reason || 'Account deaktiviert'
              }
              user.value = null
              if (blockStatusUnsub) {
                blockStatusUnsub()
                blockStatusUnsub = null
              }
              await signOut(auth).catch(() => {})
              const appStore = useAppStore()
              appStore.clearStore()
            }
          )
        }

        if (isLoggingOut) {
          user.value = null
          safeResolve()
          return
        }

        user.value = effectiveUser
        if (effectiveUser) {
          syncUserProfilePhoto(effectiveUser).catch(() => {})
          isAccountBlocked.value = false
          blockedDetails.value = null
          try {
            localStorage.setItem('auth_user_cache', JSON.stringify({ email: effectiveUser.email, uid: effectiveUser.uid }))
            localStorage.setItem('was_authenticated', '1')
          } catch {}
        } else {
          try {
            localStorage.removeItem('auth_user_cache')
            localStorage.removeItem('was_authenticated')
          } catch {}
        }
        
        // Setup or teardown Firestore sync depending on auth state
        const appStore = useAppStore()
        if (effectiveUser) {
          appStore.initialize()
        } else {
          appStore.loadDemoData?.()
        }

        safeResolve()
      })
    })

    return initPromise
  }

  const clearBlockedState = () => {
    isAccountBlocked.value = false
    blockedDetails.value = null
  }

  const createGoogleProvider = () => {
    const provider = new GoogleAuthProvider()
    provider.addScope?.('profile')
    provider.addScope?.('email')
    provider.setCustomParameters({ prompt: 'select_account' })
    return provider
  }

  const loginWithGoogleRedirect = async () => {
    clearBlockedState()
    const provider = createGoogleProvider()
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('auth_redirect_pending', '1')
    }
    try {
      await signInWithRedirect(auth, provider, browserPopupRedirectResolver)
    } catch (redirectErr) {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('auth_redirect_pending')
      }
      console.error('Error during Google redirect login:', redirectErr)
      throw redirectErr
    }
  }

  const loginWithGoogle = async () => {
    clearBlockedState()
    const provider = createGoogleProvider()

    try {
      const cred = await signInWithPopup(auth, provider, browserPopupRedirectResolver)
      if (cred?.user) {
        const { useAccessControl } = await import('../composables/useAccessControl')
        const { checkIfUserBlocked } = useAccessControl()
        const check = await checkIfUserBlocked({
          uid: cred.user.uid,
          email: cred.user.email
        })
        if (check.isBlocked) {
          isAccountBlocked.value = true
          blockedDetails.value = {
            email: cred.user.email || cred.user.uid,
            reason: check.reason || ''
          }
          await signOut(auth).catch(() => {})
          user.value = null
          const appStore = useAppStore()
          appStore.clearStore()
          throw new Error('ACCOUNT_BLOCKED')
        }
        syncUserProfilePhoto(cred.user).catch(() => {})
      }
    } catch (error: any) {
      if (error?.message === 'ACCOUNT_BLOCKED') {
        throw error
      }
      // If user deliberately closed popup, don't automatically trigger full-page redirect, but let caller know
      if (error?.code === 'auth/popup-closed-by-user') {
        throw error
      }
      
      // Fallback on popup environment issues (internal-error, popup-blocked, COOP, iframe, userinfo cookie blocking, network)
      const isPopupIssue =
        error?.code === 'auth/popup-blocked' ||
        error?.code === 'auth/cancelled-popup-request' ||
        error?.code === 'auth/internal-error' ||
        error?.code === 'auth/network-request-failed' ||
        (error?.code === 'auth/invalid-credential' && error?.message?.includes('userinfo')) ||
        error?.message?.includes('Cross-Origin-Opener-Policy') ||
        error?.message?.includes('popup') ||
        error?.message?.includes('internal-error') ||
        error?.message?.includes('window')

      if (isPopupIssue) {
        console.warn('Popup authentication encountered an issue, falling back to redirect flow:', error?.message || error?.code)
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('auth_redirect_pending', '1')
        }
        try {
          await signInWithRedirect(auth, provider, browserPopupRedirectResolver)
          return
        } catch (redirectErr) {
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem('auth_redirect_pending')
          }
          console.error('Error during redirect login fallback:', redirectErr)
          throw redirectErr
        }
      }
      console.error('Error logging in with Google:', error)
      throw error
    }
  }

  const logout = async () => {
    isLoggingOut = true
    try {
      if (blockStatusUnsub) {
        blockStatusUnsub()
        blockStatusUnsub = null
      }
      user.value = null
      isRestoringSession.value = false
      clearStoredAuthSession()
      try {
        localStorage.removeItem('last_used_route')
      } catch {}
      await signOut(auth)
      const appStore = useAppStore()
      appStore.clearStore()
      appStore.loadDemoData?.(true)
    } catch (error) {
      console.error('Error logging out:', error)
      throw error
    } finally {
      isLoggingOut = false
    }
  }

  return {
    user,
    userPhotoURL,
    syncUserProfilePhoto,
    refreshUserProfile,
    isAuthReady,
    isRestoringSession,
    hasStoredAuthSession,
    clearStoredAuthSession,
    isAdmin,
    isAccountBlocked,
    blockedDetails,
    redirectAuthError,
    clearBlockedState,
    initializeAuth,
    loginWithGoogle,
    loginWithGoogleRedirect,
    logout
  }
})
